/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { embedDashboard, embedSession } from 'amazon-quicksight-embedding-sdk';
import { Select, Spin } from 'antd';
import { convertLocaleToIso } from '@moodys/cre-cpm.functions.localization';
import { Testable } from '@moodys/cre-cpm.models.testable';
import { getTestId } from '@moodys/cre-cpm.functions.testing';
import { useQuery } from '@moodys/cre-cpm.hooks.use-query';
import {
  authApi,
  Language,
  QuickSight,
  User,
} from '@moodys/cre-cpm-client-apis.apis.services.auth';
import { DashboardOptions } from './models';
import styles from './quicksight.module.less';

const { Option } = Select;
let dashboard: any;

export interface QuickSightProps extends Testable {
  user: User
  locale: Language
  isAuthor: boolean
  handleError: (error?: unknown) => void
  onNotAvailable: JSX.Element
  onLoading: JSX.Element
  onError: JSX.Element
}

export const QuickSightAdapter: React.FC<QuickSightProps> = ({
  user,
  locale,
  isAuthor,
  handleError,
  onNotAvailable,
  onLoading,
  onError,
  testId,
}) => {
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [dashboardData, setDashboardData] = useState<QuickSight | undefined>();
  const [, isUserEmbeddedLoading, , , request] = useQuery(
    // User must be defined with the session, so here never is undefined
    () => authApi.getEmbededQuiksightUrl(user.id!.toString()),
    {
      disabled: true,
      onSuccess: (puser) => {
        if (puser.data?.attributes && puser.data?.attributes?.quicksight_embed_url) {
          setDashboardData(puser.data.attributes.quicksight_embed_url);
        }
      },
      onError: () => {
        setHasError(true);
      },
      onComplete: () => {
        setLoading(false);
      },
    },
  );

  const maxWait = 2000;

  const containerRef = React.createRef<HTMLDivElement>();

  const getOptions = (): DashboardOptions | null => (dashboardData?.embed_url
    ? {
      url: dashboardData.embed_url,
      container: containerRef.current,
      scrolling: 'no',
      parameters: {},
      locale: convertLocaleToIso(locale),
      printEnabled: true,
      footerPaddingEnabled: true,
    }
    : null);

  const handleQSError = (): void => {
    setLoading(false);
    setHasError(true);
    handleError();
  };
  const onLoad = (): void => {
    setLoading(false);
  };
  const changeDashboard = (value: string): void => {
    dashboard.navigateToDashboard({
      dashboardId: value,
    });
  };

  const createEmbedDashboard = (): void => {
    const options = getOptions();
    if (!options) return;

    if (isAuthor) {
      dashboard = embedSession(options);
    } else {
      dashboard = embedDashboard(options);
    }

    dashboard.on('error', handleQSError);
    dashboard.on('load', onLoad);
    setTimeout(() => onLoad(), maxWait);
  };
  useEffect(() => {
    if (dashboardData) {
      createEmbedDashboard();
    }
  }, [dashboardData]);

  useEffect(() => {
    request();
  }, []);

  const renderResult = (): JSX.Element => {
    if (isUserEmbeddedLoading || loading) {
      return onLoading;
    }
    if (hasError) {
      return onError;
    }

    return onNotAvailable;
  };

  return dashboardData?.embed_url && !hasError
    ? (
      <div
        className={styles.container}
        data-testid={getTestId('rxd_quicksight__container', testId)}
      >
        <Spin spinning={loading} data-testid={getTestId('rxd_quicksight__spin', testId)}>
          {!isAuthor && dashboardData?.dashboards?.length > 1 && (
            <Select
              onChange={changeDashboard}
              defaultValue={dashboardData?.dashboards[0].dashboard_id}
              className={styles.select}
              data-testid={getTestId('rxd_quicksight__select_dashboard', testId)}
            >
              {dashboardData?.dashboards?.map((dashboardObj: any) => (
                <Option
                  key={dashboardObj.dashboard_id}
                  value={dashboardObj.dashboard_id}
                  data-testid={getTestId(
                    'rxd_quicksight__select_option',
                    `${testId}__${dashboardObj.dashboard_id}`,
                  )}
                >
                  {dashboardObj.name}
                </Option>
              ))}
            </Select>
          )}
          <div
            className={styles.iframe}
            ref={containerRef}
            data-testid={getTestId('rxd_quicksight__iframe_container', testId)}
          />
        </Spin>
      </div>
    ) : (
      renderResult()
    );
};
